import axios from 'axios';
import { isEmpty } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { INSIGHTS_SORT_LIST_INSTAGRAM } from 'utils/insights';
import { INFLUENCER_TYPES } from 'utils/influencers';
import { dataGetCommonStructure } from 'utils/data/data';
import { __t, applyTranslations } from 'utils/language';
import { getInstagramThumbnailUrl } from 'utils/content/thumbnails';

import type {
  Dispatch,
  GetState,
  ThunkAction,
  TypedAction
} from 'types';
import type {
  GeneralFilter,
  Post,
  PostResponse,
  PostState,
  Report,
  ReportType
} from 'modules/insights/reports/types';

applyTranslations(require('components/influencer-verification-label/influencer-verification-label.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

const { INSIGHTS_REPORTS } = API.END_POINTS;

export const INSTAGRAM_URL = 'https://instagram.com/';

const POSTS_LOADING = 'insights-reports/POSTS_LOADING';
const POSTS_LOADED = 'insights-reports/POSTS_LOADED';
const POSTS_UPDATE_SCREEN_STATE = 'insights-reports/POSTS_UPDATE_SCREEN_STATE';
const POSTS_UPDATE_FILTERS_SCREEN_STATE = 'insights-reports/POSTS_UPDATE_FILTERS_SCREEN_STATE';
const POSTS_RESET = 'insights-reports/POSTS_RESET';

const ALL_PROFILES = ['verified', 'unverified'];
const PROFILES_MSG_KEY = 'influencer_verification_label.display_profiles';
const COMMON_MSG_KEY = 'collabary_common';

const initialState: PostState = {
  posts: [],
  isLoading: false,
  screenState: {
    page: 1,
    sort: Object.keys(INSIGHTS_SORT_LIST_INSTAGRAM)[0],
    filters: {
      queryIds: [],
      profileTypes: ALL_PROFILES.map(key => (
        dataGetCommonStructure(
          key,
          __t(`${PROFILES_MSG_KEY}.${key}`),
          'false',
          false
        )
      )),
      tiers: INFLUENCER_TYPES.map(key => (
        dataGetCommonStructure(
          key,
          __t(`${COMMON_MSG_KEY}.influencer_tiers.${key}`),
          'false',
          false
        )
      ))
    },
    channel: '',
    pagination: {}
  }
};

type Status = {
  status: boolean
};

const setLoadingStatus = (
  type: string,
  status: boolean
): TypedAction<Status> => ({
  type,
  payload: {
    status
  }
});

type Results = {
  results: PostResponse
};

const postsLoaded = (
  results: PostResponse
): TypedAction<Results> => ({
  type: POSTS_LOADED,
  payload: {
    results
  }
});

type ScreenState = {
  value: string,
  propName: string
};

export const postsUpdateScreenState = (
  value: string,
  propName: string
): TypedAction<ScreenState> => ({
  type: POSTS_UPDATE_SCREEN_STATE,
  payload: {
    value,
    propName
  }
});

export const postsUpdateFiltersScreenState = (
  value: string,
  propName: string
): TypedAction<ScreenState> => ({
  type: POSTS_UPDATE_FILTERS_SCREEN_STATE,
  payload: {
    value,
    propName
  }
});

type Reset = {
  type: typeof POSTS_RESET
};

export const resetPosts = (): TypedAction<Reset> => ({
  type: POSTS_RESET
});

const preloadPostsImages = (
  allPosts: Array<Post>
) => {
  const promises = (allPosts || []).map(post => (
    getInstagramThumbnailUrl(post.url, '', post)
      .then(url => ({
        ...post,
        assetUrl: url
      }))
      .catch(() => ({ ...post, assetUrl: null }))
  ));

  return Promise.all(promises);
};

const getProfilesQueryParam = (
  profileTypes: Array<GeneralFilter>
): Array<string> => {
  const selectedProfileTypes = profileTypes.filter(profile => profile.isSelected);

  return selectedProfileTypes.map(profile => `&${profile.identifier}=true`);
};

const getTiersQueryParam = (tiers: Array<GeneralFilter>): string => {
  const selectedTiers = tiers.filter(tier => tier.isSelected);

  return !isEmpty(selectedTiers)
    ? `&follower_tiers=${selectedTiers.map(tier => tier.identifier).join(',')}`
    : '';
};

let postsCancelToken;

export const fetchPosts = (
  report: Report,
  reportType: ReportType
): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    dispatch(setLoadingStatus(POSTS_LOADING, true));

    // handle cancellation in case of repeated request
    if (postsCancelToken) {
      postsCancelToken.cancel();
    }
    postsCancelToken = axios.CancelToken.source();

    try {
      const postsState = getState().insightsReportsPosts;
      const screenState = postsState.screenState;

      const page = screenState.page;
      const sort = screenState.sort;
      const channel = screenState.channel;
      const queryIds = screenState.filters.queryIds;
      const profilesQueryParam = getProfilesQueryParam(screenState.filters.profileTypes);
      const tiersQueryParam = getTiersQueryParam(screenState.filters.tiers);

      const url = INSIGHTS_REPORTS.getPosts(report.id, page, queryIds, profilesQueryParam, tiersQueryParam, channel, sort, reportType);
      const response = await get(url, { cancelToken: postsCancelToken.token });
      const posts = response.data || [];

      const results = await preloadPostsImages(posts.data);

      dispatch(postsLoaded({
        ...posts,
        data: results
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      dispatch(setLoadingStatus(POSTS_LOADING, false));
      logError(error);
    } finally {
      dispatch(setLoadingStatus(POSTS_LOADING, false));
    }
  }
);

export default createReducer<PostState, any>('insightsReportsPosts', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [POSTS_LOADING]: (state: PostState, { status }: Status) => ({
    ...state,
    isLoading: status
  }),
  [POSTS_LOADED]: (state: PostState, { results }: Results) => ({
    ...state,
    posts: results.data,
    screenState: {
      ...state.screenState,
      pagination: results.meta.pagination ? results.meta.pagination : {
        ...results.meta,
        links: results.links
      }
    }
  }),
  [POSTS_UPDATE_SCREEN_STATE]: (state: PostState, { value, propName }: ScreenState) => ({
    ...state,
    screenState: {
      ...state.screenState,
      [propName]: value
    }
  }),
  [POSTS_UPDATE_FILTERS_SCREEN_STATE]: (state: PostState, { value, propName }: ScreenState) => ({
    ...state,
    screenState: {
      ...state.screenState,
      filters: {
        ...state.screenState.filters,
        [propName]: value
      }
    }
  }),
  [POSTS_RESET]: (state: PostState) => ({
    ...state,
    posts: [],
    screenState: initialState.screenState
  })
});
